import React from "react";
import { FaCaretDown, FaChevronLeft, FaChevronRight } from "react-icons/fa6";

const ProjectsListPagination = ({
  selectedRows,
  handleRowSelection,
  isOpenPagination,
  openPagination,
  paginationRef,
  totalCount,
  totalPages,
  currentPage,
  limit,
  handleProjectsPage,
}) => {
  const renderPageButtons = () => {
    const maxButtonsToShow = 3;
    const buttons = [];

    const addPageButton = (pageNumber) => {
      const isActive = pageNumber === currentPage;
      const buttonClass = `flex items-center justify-center rounded-lg text-center font-medium text-neutral-100 transition-colors dark:text-neutral-0 h-8 w-8 text-sm ${
        isActive
          ? "bg-neutral-20 dark:bg-neutral-80"
          : "hover:bg-neutral-10 dark:hover:bg-neutral-90"
      }`;

      buttons.push(
        <div
          key={pageNumber}
          className="focus:outline-none"
          id={`headlessui-radiogroup-option-${pageNumber}`}
          role="radio"
          aria-checked={isActive ? "true" : "false"}
          tabIndex="0"
          data-headlessui-state={isActive ? "checked" : ""}
        >
          <button
            type="button"
            className={buttonClass}
            onClick={() => handleProjectsPage(pageNumber)}
          >
            {pageNumber}
          </button>
        </div>
      );
    };

    if (totalPages <= maxButtonsToShow * 2 + 1) {
      for (let i = 1; i <= totalPages; i++) {
        addPageButton(i);
      }
    } else {
      addPageButton(1);
      if (currentPage > 2) {
        buttons.push(
          <div
            key="ellipsisStart"
            className="flex items-center justify-center h-8 w-8"
          >
            <span className="text-neutral-100 dark:text-neutral-0">...</span>
          </div>
        );
      }

      for (
        let i = Math.max(currentPage - maxButtonsToShow, 2);
        i <= Math.min(currentPage + maxButtonsToShow, totalPages - 1);
        i++
      ) {
        addPageButton(i);
      }

      if (currentPage < totalPages - 1) {
        buttons.push(
          <div
            key="ellipsisEnd"
            className="flex items-center justify-center h-8 w-8"
          >
            <span className="text-neutral-100 dark:text-neutral-0">...</span>
          </div>
        );
      }

      addPageButton(totalPages);
    }

    return buttons;
  };
  return (
    <div className="flex w-full items-center  p-4 xs:justify-center sm:justify-center md:justify-between lg:justify-between xl:justify-between">
      <div className="text-sm font-normal text-neutral-100 transition-colors dark:text-neutral-0 xs:hidden sm:hidden md:block lg:block xl:block">
        Showing {Math.min(1 + (currentPage - 1) * limit, totalCount)}-
        {Math.min(currentPage * limit, totalCount)} out of {totalCount} items
      </div>
      <nav
        role="navigation"
        aria-label="Pagination Navigation"
        className="flex items-center gap-2"
      >
        <button
          type="button"
          disabled={currentPage === 1}
          className={`flex items-center justify-center rounded-lg text-center font-medium text-neutral-100 transition-colors h-8 w-8 text-sm ${
            currentPage === 1
              ? "dark:text-neutral-90"
              : "dark:text-neutral-0 hover:bg-neutral-10 dark:hover:bg-neutral-90"
          }`}
          onClick={() => handleProjectsPage(currentPage - 1)}
        >
          <FaChevronLeft />
        </button>
        {renderPageButtons()}

        <button
          type="button"
          disabled={currentPage === totalPages}
          className={`flex items-center justify-center rounded-lg text-center font-medium text-neutral-100 transition-colors h-8 w-8 text-sm ${
            currentPage === totalPages
              ? "dark:text-neutral-90"
              : "dark:text-neutral-0 hover:bg-neutral-10 dark:hover:bg-neutral-90"
          }`}
          onClick={() => handleProjectsPage(currentPage + 1)}
        >
          <FaChevronRight />
        </button>
      </nav>

      <div className="flex items-center gap-2 whitespace-nowrap text-sm text-neutral-100 transition-colors dark:text-neutral-0 xs:hidden sm:hidden md:flex lg:flex xl:flex">
        Show rows
        <div
          ref={paginationRef}
          data-headlessui-state={`${isOpenPagination ? "open" : ""}`}
        >
          <div className="relative z-dropdown w-full">
            <button
              className="group w-full outline-none"
              id="headlessui-listbox-button-:r25k:"
              type="button"
              aria-haspopup="listbox"
              aria-expanded="false"
              data-headlessui-state={`${isOpenPagination ? "open" : ""}`}
              aria-controls={`${
                isOpenPagination ? "headlessui-listbox-options-:r26t:" : ""
              }`}
            >
              <div
                onClick={openPagination}
                className="flex items-center justify-between gap-1 p-[5px] pl-3 pr-2 font-medium transition-colors text-sm w-full text-neutral-100 dark:text-neutral-0 border ui-not-open:border-neutral-20 ui-not-open:bg-neutral-0 ui-not-open:dark:border-neutral-80 ui-not-open:dark:bg-neutral-100 ui-not-open:hover:bg-neutral-5 ui-not-open:dark:hover:bg-neutral-95 ui-open:hover:bg-neutral-5 ui-open:dark:hover:bg-neutral-95 ui-open:border-neutral-40 ui-open:bg-neutral-10 dark:ui-open:border-neutral-60 dark:ui-open:bg-neutral-90 ui-disabled:border-neutral-20 ui-disabled:bg-neutral-10 ui-disabled:text-neutral-40 dark:ui-disabled:border-neutral-80 dark:ui-disabled:bg-neutral-90 dark:ui-disabled:text-neutral-60"
                role="button"
                tabindex="0"
              >
                <div className="truncate">{limit}</div>
                <FaCaretDown className="text-neutral-70 transition-colors dark:text-neutral-30" />
              </div>
            </button>
            <div
              id="headlessui-portal-root"
              className={`${isOpenPagination ? "block" : "hidden"}`}
            >
              <div data-headlessui-portal="">
                <ul
                  className="outline-none transform opacity-100"
                  aria-labelledby="headlessui-listbox-button-:r25k:"
                  aria-orientation="vertical"
                  id="headlessui-listbox-options-:r26t:"
                  role="listbox"
                  tabindex="0"
                  data-headlessui-state="open"
                  aria-activedescendant="headlessui-listbox-option-:r26v:"
                  style={{
                    position: "absolute",
                    zIndex: "9999",
                    top: "-111px",
                    left: "0px",
                  }}
                >
                  <div className="w-full min-w-fit overflow-hidden border border-neutral-20 bg-neutral-0 py-1 shadow-md shadow-black-5 dark:border-neutral-80  dark:bg-neutral-100 dark:shadow-black-40">
                    <li
                      className="relative cursor-pointer"
                      onClick={() => handleRowSelection(5)}
                      id="headlessui-listbox-option-:r26u:"
                      role="option"
                      tabindex="-1"
                      aria-selected={limit === 5}
                      data-headlessui-state={`${
                        limit === 5 ? "active selected" : ""
                      }`}
                    >
                      <div className="relative cursor-pointer overflow-hidden bg-neutral-0 px-3 py-1.5 transition-colors dark:bg-neutral-100 ui-selected:bg-neutral-10 dark:ui-selected:bg-neutral-90 ui-active:ui-not-selected:bg-neutral-5 dark:ui-active:ui-not-selected:bg-neutral-95 dark:hover:bg-neutral-95 hover:bg-neutral-5">
                        <div className="absolute left-0 top-0 h-[100px] w-[3px] transition-colors ui-selected:bg-neutral-100 dark:ui-selected:bg-neutral-0"></div>
                        <div className="font-normal text-neutral-100 transition-colors dark:text-neutral-0 text-sm">
                          5
                        </div>
                      </div>
                    </li>
                    <li
                      className="relative cursor-pointer"
                      onClick={() => handleRowSelection(10)}
                      id="headlessui-listbox-option-:r26v:"
                      role="option"
                      tabindex="-1"
                      aria-selected={limit === 10}
                      data-headlessui-state={`${
                        limit === 10 ? "active selected" : ""
                      }`}
                    >
                      <div className="relative cursor-pointer overflow-hidden bg-neutral-0 px-3 py-1.5 transition-colors dark:bg-neutral-100 ui-selected:bg-neutral-10 dark:ui-selected:bg-neutral-90 ui-active:ui-not-selected:bg-neutral-5 dark:ui-active:ui-not-selected:bg-neutral-95 dark:hover:bg-neutral-95 hover:bg-neutral-5">
                        <div className="absolute left-0 top-0 h-[100px] w-[3px] transition-colors ui-selected:bg-neutral-100 dark:ui-selected:bg-neutral-0"></div>
                        <div className="font-normal text-neutral-100 transition-colors dark:text-neutral-0 text-sm">
                          10
                        </div>
                      </div>
                    </li>
                    <li
                      className="relative cursor-pointer"
                      onClick={() => handleRowSelection(20)}
                      id="headlessui-listbox-option-:r270:"
                      role="option"
                      tabindex="-1"
                      aria-selected={limit === 20}
                      data-headlessui-state={`${
                        limit === 20 ? "active selected" : ""
                      }`}
                    >
                      <div className="relative cursor-pointer overflow-hidden bg-neutral-0 px-3 py-1.5 transition-colors dark:bg-neutral-100 ui-selected:bg-neutral-10 dark:ui-selected:bg-neutral-90 ui-active:ui-not-selected:bg-neutral-5 dark:ui-active:ui-not-selected:bg-neutral-95 dark:hover:bg-neutral-95 hover:bg-neutral-5">
                        <div className="absolute left-0 top-0 h-[100px] w-[3px] transition-colors ui-selected:bg-neutral-100 dark:ui-selected:bg-neutral-0"></div>
                        <div className="font-normal text-neutral-100 transition-colors dark:text-neutral-0 text-sm">
                          20
                        </div>
                      </div>
                    </li>
                  </div>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProjectsListPagination;
